import { ArrowLeft, Shield, Eye, Database, Cookie, Mail, Globe, UserX, Baby } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const lastUpdated = "January 29, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-secondary-700 hover:text-secondary-900 hover:bg-secondary-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-full">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-500">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed">
              Sundog Rosie ("we," "our," or "us") operates the Radiant 40 application. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website.
            </p>

            <div className="bg-primary/5 rounded-xl p-6 my-8">
              <p className="text-gray-700 font-medium m-0">
                By using Radiant 40, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use our services.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Database className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">Information We Collect</h2>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">Personal Information You Provide</h3>
            <p className="text-gray-700">When you register for an account, we collect:</p>
            <ul className="text-gray-700">
              <li><strong>Account Information:</strong> Name, email address, and password</li>
              <li><strong>Payment Information:</strong> Processed securely through third-party payment processors (Stripe, Apple, Google). We do not store your credit card numbers.</li>
              <li><strong>Profile Information:</strong> Any additional information you choose to add to your profile</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">Information Collected Automatically</h3>
            <ul className="text-gray-700">
              <li><strong>Usage Data:</strong> Pages visited, features used, tasks completed, time spent in app</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
              <li><strong>Log Data:</strong> IP address, access times, app crashes and errors</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">User-Generated Content</h3>
            <ul className="text-gray-700">
              <li>Community posts and comments</li>
              <li>Photos uploaded to your personal gallery</li>
              <li>Progress tracking data and completed tasks</li>
            </ul>

            {/* How We Use Your Information */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Eye className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">How We Use Your Information</h2>
            </div>

            <p className="text-gray-700">We use the information we collect to:</p>
            <ul className="text-gray-700">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Track your progress through the 12-week program</li>
              <li>Personalize your experience and content</li>
              <li>Monitor and analyze usage trends</li>
              <li>Detect, prevent, and address technical issues</li>
            </ul>

            {/* Data Sharing */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Globe className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">How We Share Your Information</h2>
            </div>

            <p className="text-gray-700">We do NOT sell your personal information. We may share information with:</p>
            <ul className="text-gray-700">
              <li><strong>Service Providers:</strong> Third parties that help us operate our services (payment processors, hosting providers)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you explicitly agree to share information</li>
            </ul>

            <div className="bg-green-50 rounded-xl p-6 my-6">
              <p className="text-green-800 font-medium m-0">
                Your community posts are visible to other Radiant 40 users. Your personal gallery photos are private and only visible to you.
              </p>
            </div>

            {/* Data Retention */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">Data Retention</h2>
            <p className="text-gray-700">
              We retain your personal information for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time through the Account Settings page.
            </p>

            {/* Cookies */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Cookie className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">Cookies and Tracking</h2>
            </div>
            <p className="text-gray-700">
              We use cookies and similar technologies to maintain your session, remember your preferences, and understand how you use our services. You can control cookies through your browser settings.
            </p>

            {/* Your Rights */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">Your Rights</h2>
            <p className="text-gray-700">Depending on your location, you may have the right to:</p>
            <ul className="text-gray-700">
              <li>Access the personal information we hold about you</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Delete your personal information</li>
              <li>Export your data in a portable format</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>

            {/* Delete Account */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <UserX className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">Account Deletion</h2>
            </div>
            <p className="text-gray-700">
              You may delete your account at any time through the Account Settings page in the app. Upon deletion, we will remove your personal information within 30 days, except where retention is required by law or for legitimate business purposes.
            </p>

            {/* Children */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Baby className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">Children's Privacy</h2>
            </div>
            <p className="text-gray-700">
              Radiant 40 is designed for adults and is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child, please contact us immediately.
            </p>

            {/* International */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">International Users</h2>
            <p className="text-gray-700">
              If you are accessing our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States. By using our services, you consent to this transfer.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">For European Users (GDPR)</h3>
            <p className="text-gray-700">
              If you are in the European Economic Area, you have additional rights under GDPR including the right to lodge a complaint with a supervisory authority. Our legal basis for processing is contract performance (providing our services) and legitimate interests (improving our services).
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6">For California Residents (CCPA)</h3>
            <p className="text-gray-700">
              California residents have the right to know what personal information is collected, request deletion, and opt-out of the sale of personal information. We do not sell personal information.
            </p>

            {/* Security */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">Data Security</h2>
            <p className="text-gray-700">
              We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            {/* Changes */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. Continued use of the app after changes constitutes acceptance.
            </p>

            {/* Contact */}
            <div className="flex items-start gap-3 mt-8 mb-4">
              <Mail className="w-6 h-6 text-primary shrink-0 mt-1" />
              <h2 className="text-2xl font-serif font-bold text-gray-900 m-0">Contact Us</h2>
            </div>
            <p className="text-gray-700">
              If you have questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 mt-4">
              <p className="text-gray-700 m-0">
                <strong>Sundog Rosie</strong><br />
                Email: <a href="mailto:sundog.rose@gmail.com" className="text-primary hover:underline">sundog.rose@gmail.com</a><br />
                United States
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mt-8 text-gray-500">
          © 2026 Sundog Rosie. All rights reserved.
        </div>
      </div>
    </div>
  );
}
